import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

/**
 * OIDC wiring for Zitadel. Values come from the runtime `window.config` (never build-time
 * env), matching the app's config convention. When `oidcEnabled` is false the app uses the
 * legacy password-free email sign-in instead of mounting the OIDC provider at all.
 *
 * The flow is Authorization Code + PKCE for a public SPA client: no secret, PKCE (S256) is
 * the interception defense, and `offline_access` yields a rotating refresh token so
 * `automaticSilentRenew` keeps the session alive without re-prompting. Tokens live in
 * localStorage (userStore) so a reload/restart stays signed in — acceptable for an internal
 * VPN tool; the ID token we send to the backend is short-lived and strictly validated there.
 */
export const oidcEnabled: boolean = !!window.config.oidcIssuer;

export const oidcConfig: AuthProviderProps = {
  authority: window.config.oidcIssuer,
  client_id: window.config.oidcClientId,
  redirect_uri: window.location.origin + '/auth/callback',
  post_logout_redirect_uri: window.location.origin + '/',
  response_type: 'code',
  scope: 'openid profile email offline_access',
  automaticSilentRenew: true,
  loadUserInfo: true,
  // Skip the check_session iframe (needs 3rd-party cookies); refresh-token renewal covers us.
  monitorSession: false,
  revokeTokensOnSignout: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Strip ?code&state from the URL but KEEP the /auth/callback path, so the AuthCallback route
  // (in app.tsx) can restore the originally-requested deep link (see RETURN_TO_KEY) instead of
  // always landing on the boards index.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
